/**
 * RenewalComparison Component
 *
 * Shows a side-by-side comparison of current contract vs proposed renewal.
 * Highlights changes in member count, features, and pricing with visual
 * indicators for increases, decreases, and new additions.
 *
 * Used by Sales/CSM during renewal discussions to clearly show value changes.
 *
 * @see src/modules/RetoolModule/components/PricingCalculator/PricingCalculatorFormV2.tsx
 */

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  alpha,
  useTheme,
  Chip,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Plus as PlusIcon,
  Minus as MinusIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  RefreshCw as RefreshIcon,
  Check as CheckIcon,
  X as XIcon,
  ArrowRight as ArrowRightIcon,
} from 'lucide-react';
import {
  PricingCalculatorSummary,
  TenantType,
  isDedicatedTenant,
} from '../../types/pricingCalculator';

// Format currency helper
const formatCurrency = (amount: number) =>
  `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Format percentage
const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;

// Calculate percentage change
const calcPercentChange = (current: number, proposed: number): number => {
  if (current === 0) return proposed > 0 ? 100 : 0;
  return ((proposed - current) / current) * 100;
};

interface ComparisonRowProps {
  label: string;
  currentValue: string | number;
  proposedValue: string | number;
  isCurrency?: boolean;
  isNew?: boolean;
  isRemoved?: boolean;
  showPercent?: boolean;
}

const ComparisonRow: React.FC<ComparisonRowProps> = ({
  label,
  currentValue,
  proposedValue,
  isCurrency = false,
  isNew = false,
  isRemoved = false,
  showPercent = true,
}) => {
  const theme = useTheme();

  const current = typeof currentValue === 'number' ? currentValue : parseFloat(currentValue) || 0;
  const proposed =
    typeof proposedValue === 'number' ? proposedValue : parseFloat(proposedValue) || 0;
  const diff = proposed - current;
  const percentChange = calcPercentChange(current, proposed);

  const isIncrease = diff > 0;
  const isDecrease = diff < 0;
  const isUnchanged = diff === 0 && !isNew && !isRemoved;

  const getDiffColor = () => {
    if (isNew) return theme.palette.info.main;
    if (isRemoved) return theme.palette.error.main;
    if (isIncrease) return theme.palette.success.main;
    if (isDecrease) return theme.palette.warning.main;
    return theme.palette.text.secondary;
  };

  const getDiffIcon = () => {
    if (isNew) return <PlusIcon size={14} />;
    if (isRemoved) return <MinusIcon size={14} />;
    if (isIncrease) return <TrendingUpIcon size={14} />;
    if (isDecrease) return <TrendingDownIcon size={14} />;
    return null;
  };

  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;
    return isCurrency ? formatCurrency(val) : val.toLocaleString();
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 100px 24px 100px 120px',
        alignItems: 'center',
        py: 1,
        px: 1.5,
        borderRadius: '6px',
        backgroundColor: isNew
          ? alpha(theme.palette.info.main, 0.05)
          : isRemoved
            ? alpha(theme.palette.error.main, 0.05)
            : 'transparent',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}
        {isNew && (
          <Chip
            label="NEW"
            size="small"
            color="info"
            sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
          />
        )}
        {isRemoved && (
          <Chip
            label="REMOVED"
            size="small"
            color="error"
            sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
          />
        )}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'right',
          fontFamily: 'monospace',
          color: isRemoved ? theme.palette.text.secondary : theme.palette.text.primary,
          textDecoration: isRemoved ? 'line-through' : 'none',
        }}
      >
        {formatValue(currentValue)}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ArrowRightIcon size={16} color={theme.palette.text.disabled} />
      </Box>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'right',
          fontFamily: 'monospace',
          fontWeight: isUnchanged ? 400 : 600,
          color: isNew ? theme.palette.info.main : theme.palette.text.primary,
        }}
      >
        {formatValue(proposedValue)}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          color: getDiffColor(),
        }}
      >
        {getDiffIcon()}
        {!isNew && !isRemoved && !isUnchanged && (
          <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {isCurrency ? formatCurrency(diff) : diff.toLocaleString()}
            {showPercent && ` (${formatPercent(percentChange)})`}
          </Typography>
        )}
        {isUnchanged && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            No change
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface FeatureComparisonProps {
  label: string;
  currentEnabled: boolean;
  proposedEnabled: boolean;
}

const FeatureComparison: React.FC<FeatureComparisonProps> = ({
  label,
  currentEnabled,
  proposedEnabled,
}) => {
  const theme = useTheme();

  const isNew = !currentEnabled && proposedEnabled;
  const isRemoved = currentEnabled && !proposedEnabled;
  const isUnchanged = currentEnabled === proposedEnabled;

  if (isUnchanged && !currentEnabled) return null; // Don't show features that are off in both

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.5,
        borderRadius: '6px',
        backgroundColor: isNew
          ? alpha(theme.palette.info.main, 0.05)
          : isRemoved
            ? alpha(theme.palette.error.main, 0.05)
            : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {isNew && (
          <Chip label="NEW" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem' }} />
        )}
        {isRemoved && (
          <Chip
            label="REMOVED"
            size="small"
            color="error"
            sx={{ height: 18, fontSize: '0.6rem' }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {currentEnabled ? (
            <CheckIcon size={14} color={theme.palette.success.main} />
          ) : (
            <XIcon size={14} color={theme.palette.text.disabled} />
          )}
        </Box>
        <ArrowRightIcon size={14} color={theme.palette.text.disabled} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {proposedEnabled ? (
            <CheckIcon size={14} color={theme.palette.success.main} />
          ) : (
            <XIcon size={14} color={theme.palette.text.disabled} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export interface CurrentContractData {
  numberOfMembers: number;
  numberOfInternalContractors: number;
  numberOfExternalContractors: number;
  numberOfGuests: number;
  subscriptionDuration: number;
  totalPrice: number;
  // Pricing configuration (needed for accurate baseline editing)
  planType: string;
  pricePerMember: number;
  discountOnMemberPrice: number;
  platformFee: number;
  discountOnPlatformFee: number;
  onboardingFee: number;
  discountOnOnboardingFee: number;
  additionalEnterpriseSupportHours: number;
  // Features
  securityPackageSso: boolean;
  discountOnSecurityPackageSso: number;
  isolatedDatabase: boolean;
  discountOnIsolatedDatabase: number;
  isolatedDatabaseMigration: boolean;
  directDatabaseIntegration: boolean;
  discountOnDirectDatabase: number;
  tenantType: TenantType;
  discountOnTenant: number;
  govCloud: boolean;
  discountOnGovCloudFee: number;
  aiForecasting: boolean;
  discountOnAiForecasting: number;
  isolatedAi: boolean;
  nonPeopleResources: boolean;
  discountOnNonPeopleResources: number;
  profileBuilding: boolean;
  discountOnProfileBuilding: number;
  skillsTagging: boolean;
  discountOnSkillsTagging: number;
  solutionsSupport: boolean;
  solutionsSupportHours: number;
  enableWhiteLabel: boolean;
  // Integrations
  selectedIntegrations: string[];
  discountOnIntegrationSetup: number;
  discountOnIntegrationRecurring: number;
  integrationMigration: boolean;
  enable4xDataTransfer: boolean;
  bakIntegrationFetchTimes: number;
}

/** Seat-count and contract-duration deltas. */
const buildScaleChanges = (
  currentContract: CurrentContractData,
  proposedMembers: number,
  proposedDuration: number,
): string[] => {
  const changesList: string[] = [];

  // Member changes
  const memberDiff = proposedMembers - currentContract.numberOfMembers;
  if (memberDiff !== 0) {
    const pct = calcPercentChange(currentContract.numberOfMembers, proposedMembers);
    changesList.push(
      `${memberDiff > 0 ? 'Added' : 'Reduced'} ${Math.abs(memberDiff)} members (${formatPercent(pct)})`,
    );
  }

  // Duration change
  if (proposedDuration !== currentContract.subscriptionDuration) {
    changesList.push(
      `Changed contract duration: ${currentContract.subscriptionDuration} → ${proposedDuration} months`,
    );
  }

  return changesList;
};

/** Dedicated-tenancy adoption or a move between Single and Enterprise tiers. */
const buildTenancyChanges = (
  currentContract: CurrentContractData,
  proposedTenantType: TenantType,
): string[] => {
  const changesList: string[] = [];

  if (!isDedicatedTenant(currentContract.tenantType) && isDedicatedTenant(proposedTenantType)) {
    changesList.push(
      `Enabled ${
        proposedTenantType === TenantType.ENTERPRISE ? 'Enterprise' : 'Single'
      } Tenant Environment (new feature)`,
    );
  } else if (
    currentContract.tenantType !== proposedTenantType &&
    isDedicatedTenant(currentContract.tenantType) &&
    isDedicatedTenant(proposedTenantType)
  ) {
    changesList.push(
      `Changed dedicated tenant tier: ${
        currentContract.tenantType === TenantType.ENTERPRISE ? 'Enterprise' : 'Single'
      } → ${proposedTenantType === TenantType.ENTERPRISE ? 'Enterprise' : 'Single'}`,
    );
  }

  return changesList;
};

type ProposedSecurityFlags = {
  proposedSecurityPackageSso: boolean;
  proposedGovCloud: boolean;
};

type ProposedAddOnFlags = {
  proposedDirectDatabaseIntegration: boolean;
  proposedAiForecasting: boolean;
  proposedIsolatedAi: boolean;
};

/**
 * Security features newly switched on for the renewal (off -> on only). Split from
 * the add-on flags below because the rendered change list interleaves the tenancy
 * changes between the two groups.
 */
const buildSecurityFeatureChanges = (
  currentContract: CurrentContractData,
  { proposedSecurityPackageSso, proposedGovCloud }: ProposedSecurityFlags,
): string[] => {
  const changesList: string[] = [];

  if (!currentContract.securityPackageSso && proposedSecurityPackageSso) {
    changesList.push('Enabled SSO (new feature)');
  }
  if (!currentContract.govCloud && proposedGovCloud) {
    changesList.push('Enabled Gov Cloud (new feature)');
  }

  return changesList;
};

/** Data and AI add-ons newly switched on for the renewal (off -> on only). */
const buildAddOnChanges = (
  currentContract: CurrentContractData,
  {
    proposedDirectDatabaseIntegration,
    proposedAiForecasting,
    proposedIsolatedAi,
  }: ProposedAddOnFlags,
): string[] => {
  const changesList: string[] = [];

  if (!currentContract.directDatabaseIntegration && proposedDirectDatabaseIntegration) {
    changesList.push('Enabled Direct Database Integration (new feature)');
  }
  if (!currentContract.aiForecasting && proposedAiForecasting) {
    changesList.push('Enabled AI Forecasting (new add-on)');
  }
  if (!currentContract.isolatedAi && proposedIsolatedAi) {
    changesList.push('Enabled Isolated AI (new add-on)');
  }

  return changesList;
};

/** Integrations added to or dropped from the contract. */
const buildIntegrationChanges = (
  currentContract: CurrentContractData,
  proposedSelectedIntegrations: string[],
): string[] => {
  const changesList: string[] = [];

  const newIntegrations = proposedSelectedIntegrations.filter(
    (i) => !currentContract.selectedIntegrations.includes(i),
  );
  const removedIntegrations = currentContract.selectedIntegrations.filter(
    (i) => !proposedSelectedIntegrations.includes(i),
  );
  if (newIntegrations.length > 0) {
    changesList.push(`Added ${newIntegrations.length} new integration(s)`);
  }
  if (removedIntegrations.length > 0) {
    changesList.push(`Removed ${removedIntegrations.length} integration(s)`);
  }

  return changesList;
};

export interface RenewalComparisonProps {
  /** Current contract data (from existing contract) */
  currentContract: CurrentContractData | null;
  /** Proposed renewal values */
  proposedMembers: number;
  proposedInternalContractors: number;
  proposedExternalContractors: number;
  proposedGuests: number;
  proposedDuration: number;
  proposedSummary: PricingCalculatorSummary | null;
  /** Proposed features */
  proposedSecurityPackageSso: boolean;
  proposedIsolatedDatabase: boolean;
  proposedDirectDatabaseIntegration: boolean;
  proposedTenantType: TenantType;
  proposedGovCloud: boolean;
  proposedAiForecasting: boolean;
  proposedIsolatedAi: boolean;
  proposedNonPeopleResources: boolean;
  proposedProfileBuilding: boolean;
  proposedSkillsTagging: boolean;
  proposedEnableWhiteLabel: boolean;
  proposedSelectedIntegrations: string[];
  /** Whether to show the comparison (only when isRenewal is true) */
  isVisible?: boolean;
}

export const RenewalComparison: React.FC<RenewalComparisonProps> = ({
  currentContract,
  proposedMembers,
  proposedInternalContractors,
  proposedExternalContractors,
  proposedGuests,
  proposedDuration,
  proposedSummary,
  proposedSecurityPackageSso,
  proposedIsolatedDatabase,
  proposedDirectDatabaseIntegration,
  proposedTenantType,
  proposedGovCloud,
  proposedAiForecasting,
  proposedIsolatedAi,
  proposedNonPeopleResources,
  proposedProfileBuilding,
  proposedSkillsTagging,
  proposedEnableWhiteLabel,
  proposedSelectedIntegrations,
  isVisible = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = React.useState(true);

  // Calculate what changed. The spread order below is the order the change list
  // is rendered in, and matches the original inline sequence exactly.
  const changes = useMemo(() => {
    if (!currentContract) return [];

    return [
      ...buildScaleChanges(currentContract, proposedMembers, proposedDuration),
      ...buildSecurityFeatureChanges(currentContract, {
        proposedSecurityPackageSso,
        proposedGovCloud,
      }),
      ...buildTenancyChanges(currentContract, proposedTenantType),
      ...buildAddOnChanges(currentContract, {
        proposedDirectDatabaseIntegration,
        proposedAiForecasting,
        proposedIsolatedAi,
      }),
      ...buildIntegrationChanges(currentContract, proposedSelectedIntegrations),
    ];
  }, [
    currentContract,
    proposedMembers,
    proposedDuration,
    proposedSecurityPackageSso,
    proposedGovCloud,
    proposedTenantType,
    proposedDirectDatabaseIntegration,
    proposedAiForecasting,
    proposedIsolatedAi,
    proposedSelectedIntegrations,
  ]);

  if (!isVisible || !currentContract) return null;

  const proposedTotal = proposedSummary?.totalCost || 0;
  const totalDiff = proposedTotal - currentContract.totalPrice;
  const totalPercentChange = calcPercentChange(currentContract.totalPrice, proposedTotal);

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: alpha(theme.palette.info.main, 0.3),
        backgroundColor: isDark
          ? alpha(theme.palette.info.main, 0.05)
          : alpha(theme.palette.info.main, 0.02),
        overflow: 'hidden',
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          '&:hover': {
            backgroundColor: alpha(theme.palette.info.main, 0.15),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RefreshIcon size={20} color={theme.palette.info.main} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Renewal Comparison
          </Typography>
          <Chip
            label={`${changes.length} change${changes.length !== 1 ? 's' : ''}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 22 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Total Change
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontFamily: 'monospace',
                color: totalDiff >= 0 ? theme.palette.success.main : theme.palette.warning.main,
              }}
            >
              {totalDiff >= 0 ? '+' : ''}
              {formatCurrency(totalDiff)} ({formatPercent(totalPercentChange)})
            </Typography>
          </Box>
          <IconButton size="small">
            {expanded ? <ChevronDownIcon size={18} /> : <ChevronRightIcon size={18} />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* What Changed Summary */}
          {changes.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: isDark
                  ? alpha(theme.palette.background.paper, 0.5)
                  : alpha(theme.palette.grey[100], 0.8),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                What Changed:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {changes.map((change, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon size={12} color={theme.palette.success.main} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {change}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Column Headers */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 24px 100px 120px',
              alignItems: 'center',
              py: 1,
              px: 1.5,
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
              mb: 1,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
              Item
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, textTransform: 'uppercase', textAlign: 'right' }}
            >
              Current
            </Typography>
            <Box />
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, textTransform: 'uppercase', textAlign: 'right' }}
            >
              Proposed
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, textTransform: 'uppercase', textAlign: 'right' }}
            >
              Change
            </Typography>
          </Box>

          {/* User Counts */}
          <ComparisonRow
            label="Members"
            currentValue={currentContract.numberOfMembers}
            proposedValue={proposedMembers}
            showPercent={true}
          />
          <ComparisonRow
            label="Internal Contractors"
            currentValue={currentContract.numberOfInternalContractors}
            proposedValue={proposedInternalContractors}
            showPercent={false}
          />
          <ComparisonRow
            label="External Contractors"
            currentValue={currentContract.numberOfExternalContractors}
            proposedValue={proposedExternalContractors}
            showPercent={false}
          />
          <ComparisonRow
            label="Guests"
            currentValue={currentContract.numberOfGuests}
            proposedValue={proposedGuests}
            showPercent={false}
          />

          <Divider sx={{ my: 1.5 }} />

          {/* Contract Details */}
          <ComparisonRow
            label="Duration (months)"
            currentValue={currentContract.subscriptionDuration}
            proposedValue={proposedDuration}
            showPercent={false}
          />

          <Divider sx={{ my: 1.5 }} />

          {/* Features */}
          <Typography variant="caption" sx={{ fontWeight: 600, px: 1.5, mb: 1, display: 'block' }}>
            FEATURES
          </Typography>
          <FeatureComparison
            label="SSO"
            currentEnabled={currentContract.securityPackageSso}
            proposedEnabled={proposedSecurityPackageSso}
          />
          <FeatureComparison
            label="Isolated Database"
            currentEnabled={currentContract.isolatedDatabase}
            proposedEnabled={proposedIsolatedDatabase}
          />
          <FeatureComparison
            label="Direct Database"
            currentEnabled={currentContract.directDatabaseIntegration}
            proposedEnabled={proposedDirectDatabaseIntegration}
          />
          <FeatureComparison
            label="Dedicated Tenant"
            currentEnabled={isDedicatedTenant(currentContract.tenantType)}
            proposedEnabled={isDedicatedTenant(proposedTenantType)}
          />
          <FeatureComparison
            label="Gov Cloud"
            currentEnabled={currentContract.govCloud}
            proposedEnabled={proposedGovCloud}
          />
          <FeatureComparison
            label="AI Forecasting"
            currentEnabled={currentContract.aiForecasting}
            proposedEnabled={proposedAiForecasting}
          />
          <FeatureComparison
            label="Isolated AI"
            currentEnabled={currentContract.isolatedAi}
            proposedEnabled={proposedIsolatedAi}
          />
          <FeatureComparison
            label="Non-People Resources"
            currentEnabled={currentContract.nonPeopleResources}
            proposedEnabled={proposedNonPeopleResources}
          />
          <FeatureComparison
            label="Profile Building"
            currentEnabled={currentContract.profileBuilding}
            proposedEnabled={proposedProfileBuilding}
          />
          <FeatureComparison
            label="Skills Tagging"
            currentEnabled={currentContract.skillsTagging}
            proposedEnabled={proposedSkillsTagging}
          />
          <FeatureComparison
            label="White Label"
            currentEnabled={currentContract.enableWhiteLabel}
            proposedEnabled={proposedEnableWhiteLabel}
          />

          <Divider sx={{ my: 1.5 }} />

          {/* Integrations */}
          <ComparisonRow
            label="Integrations"
            currentValue={currentContract.selectedIntegrations.length}
            proposedValue={proposedSelectedIntegrations.length}
            showPercent={false}
          />

          <Divider sx={{ my: 1.5 }} />

          {/* Total */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 24px 100px 120px',
              alignItems: 'center',
              py: 1.5,
              px: 1.5,
              borderRadius: '8px',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Total Contract Value
            </Typography>
            <Typography
              variant="body1"
              sx={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}
            >
              {formatCurrency(currentContract.totalPrice)}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ArrowRightIcon size={16} color={theme.palette.text.disabled} />
            </Box>
            <Typography
              variant="body1"
              sx={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}
            >
              {formatCurrency(proposedTotal)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                color: totalDiff >= 0 ? theme.palette.success.main : theme.palette.warning.main,
              }}
            >
              {totalDiff >= 0 ? <TrendingUpIcon size={16} /> : <TrendingDownIcon size={16} />}
              <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                {totalDiff >= 0 ? '+' : ''}
                {formatCurrency(totalDiff)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RenewalComparison;
